import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { motion, AnimatePresence } from 'framer-motion';
import { $canvasMode, exitCanvasMode } from '~/lib/stores/canvas';
import {
  $selectedElement,
  $selectMode,
  $selectorAvailable,
  clearSelection,
  setSelectedElement,
  type SelectedElementPayload,
} from '~/lib/stores/element-selector';
import { $editHistory, initEditHistory } from '~/lib/stores/edit-history';
import { CanvasPropertyPanel } from './CanvasPropertyPanel';
import { EditHistoryPanel } from './EditHistoryPanel';
import { previewOrigin } from '~/components/workbench/preview-url';
import { trackElementSelected } from '~/lib/element-selector/analytics';

interface CanvasModeProps {
  /** Current preview URL */
  previewUrl: string;
  /** Chat ID for API calls */
  chatId: string;
  /** Callback to reload the preview after edits */
  onReloadPreview: () => void;
}

// Validate selection payload (same as Preview.tsx)
const isValidSelectionPayload = (p: unknown): p is SelectedElementPayload => {
  const s = p as SelectedElementPayload;
  return (
    !!p &&
    typeof s.file === 'string' &&
    s.file.length <= 1024 &&
    typeof s.line === 'number' &&
    typeof s.tag === 'string' &&
    s.tag.length <= 64 &&
    typeof s.classes === 'string' &&
    s.classes.length <= 2048 &&
    typeof s.textSnippet === 'string' &&
    s.textSnippet.length <= 2048 &&
    (s.text === undefined || (typeof s.text === 'string' && s.text.length <= 2048)) &&
    (s.src === undefined || (typeof s.src === 'string' && s.src.length <= 2048)) &&
    (s.editableText === undefined || typeof s.editableText === 'boolean') &&
    typeof s.computedStyles === 'object' &&
    s.computedStyles !== null &&
    !Array.isArray(s.computedStyles) &&
    Object.keys(s.computedStyles).length <= 32
  );
};

/**
 * CanvasMode - Fullscreen Figma-like editing experience.
 *
 * Renders its own iframe pointing to the same preview URL.
 * This iframe is separate from the one in Preview.tsx, so the
 * selector bridge needs to be set up here as well.
 */
export function CanvasMode({ previewUrl, chatId, onReloadPreview }: CanvasModeProps) {
  const isActive = useStore($canvasMode);
  const selectedElement = useStore($selectedElement);
  const selectMode = useStore($selectMode);
  const editHistory = useStore($editHistory);
  const canvasIframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [canvasSelectorReady, setCanvasSelectorReady] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Initialize edit history when canvas mode becomes active
  useEffect(() => {
    if (isActive && chatId) {
      initEditHistory(chatId);
    }
  }, [isActive, chatId]);

  // Auto-enable select mode when entering canvas
  useEffect(() => {
    if (isActive && canvasSelectorReady && !selectMode) {
      $selectMode.set(true);
    }
  }, [isActive, canvasSelectorReady, selectMode]);

  // Set up message listener for canvas iframe
  useEffect(() => {
    if (!isActive || !previewUrl) {
      return undefined;
    }

    const origin = previewOrigin(previewUrl);

    if (!origin) {
      return undefined;
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== canvasIframeRef.current?.contentWindow) {
        return;
      }

      const data = event.data as { type?: string; payload?: SelectedElementPayload };

      if (data?.type === 'DUNEA_SELECTOR_READY') {
        canvasIframeRef.current?.contentWindow?.postMessage({ type: 'DUNEA_SELECTOR_INIT' }, origin);
        canvasIframeRef.current?.contentWindow?.postMessage({ type: 'DUNEA_SELECT_MODE', enabled: true }, origin);
        setCanvasSelectorReady(true);
        $selectorAvailable.set(true);
      } else if (data?.type === 'DUNEA_ELEMENT_SELECTED' && isValidSelectionPayload(data.payload)) {
        setSelectedElement(data.payload);
        trackElementSelected('canvas', data.payload.tag);
      }
    };

    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
      setCanvasSelectorReady(false);
    };
  }, [isActive, previewUrl]);

  // Sync select mode to canvas iframe
  useEffect(() => {
    if (!canvasSelectorReady || !previewUrl) {
      return undefined;
    }

    const origin = previewOrigin(previewUrl);

    if (!origin) {
      return undefined;
    }

    canvasIframeRef.current?.contentWindow?.postMessage({ type: 'DUNEA_SELECT_MODE', enabled: selectMode }, origin);

    return undefined;
  }, [selectMode, canvasSelectorReady, previewUrl]);

  // Handle escape key to exit canvas mode
  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedElement) {
          // First escape clears selection
          clearSelection();
        } else {
          // Second escape exits canvas mode
          exitCanvasMode();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, selectedElement]);

  const handleExit = useCallback(() => {
    clearSelection();
    $selectMode.set(false);
    exitCanvasMode();
  }, []);

  const handleReload = useCallback(() => {
    // Reset iframe state before reloading
    setIframeLoaded(false);
    setCanvasSelectorReady(false);

    if (canvasIframeRef.current) {
      const url = new URL(canvasIframeRef.current.src, window.location.origin);
      url.searchParams.set('_r', Date.now().toString());
      canvasIframeRef.current.src = url.toString();
    }

    onReloadPreview();
  }, [onReloadPreview]);

  if (!isActive) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        key="canvas-mode"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="fixed inset-0 z-[9999] flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
        style={{
          background: 'linear-gradient(180deg, #07070A 0%, #0A0A0E 100%)',
        }}
      >
        {/* Subtle grid background */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)
            `,
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)',
          }}
        />

        {/* Top toolbar - minimal, elegant */}
        <header className="relative z-10 flex-shrink-0 h-14 sm:h-16 flex items-center justify-between gap-2 px-3 sm:px-6">
          {/* Left: Back + Branding */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleExit}
              className="group flex items-center justify-center w-10 h-10 rounded-xl bg-surface/60 hover:bg-surface border border-hairline hover:border-subtle transition-all duration-200"
              title="Exit Canvas Mode (Esc)"
            >
              <div className="i-ph:arrow-left text-lg text-secondary group-hover:text-primary transition-colors" />
            </button>
            <div className="hidden sm:block h-6 w-px bg-hairline" />
            <div className="hidden sm:flex items-center gap-3">
              <div className="w-6 h-6 rounded-lg bg-accent/10 flex items-center justify-center">
                <div className="i-ph:paint-brush-broad text-accent text-sm" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-medium text-muted uppercase tracking-[0.14em]">Canvas</span>
                <span className="text-xs text-primary -mt-0.5">Visual Editor</span>
              </div>
            </div>
          </div>

          {/* Center: Selection status — absolutely centered, so it would collide with both groups on phones */}
          <div className="hidden md:block absolute left-1/2 -translate-x-1/2">
            <AnimatePresence mode="wait">
              {selectedElement ? (
                <motion.div
                  key="selected"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center gap-3 px-4 py-2 rounded-full bg-surface/80 border border-subtle backdrop-blur-sm"
                >
                  <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                  <span className="text-xs text-secondary">Editing</span>
                  <code className="text-xs text-primary font-mono bg-raised/50 px-2 py-0.5 rounded-md">
                    &lt;{selectedElement.tag}&gt;
                  </code>
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface/40 border border-hairline"
                >
                  <div className="i-ph:cursor-click text-sm text-muted" />
                  <span className="text-xs text-muted">Select an element to begin</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* History button */}
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-surface/60 hover:bg-surface border border-hairline hover:border-subtle transition-all duration-200"
              title="Edit History"
            >
              <div className="i-ph:clock-counter-clockwise text-lg text-secondary hover:text-primary transition-colors" />
              {editHistory.length > 0 && (
                <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-accent flex items-center justify-center">
                  <span className="text-[9px] font-bold text-black">
                    {editHistory.length > 9 ? '9+' : editHistory.length}
                  </span>
                </div>
              )}
            </button>
            <button
              type="button"
              onClick={handleReload}
              className="flex items-center justify-center w-10 h-10 rounded-xl bg-surface/60 hover:bg-surface border border-hairline hover:border-subtle transition-all duration-200"
              title="Reload preview"
            >
              <div className="i-ph:arrows-clockwise text-lg text-secondary hover:text-primary transition-colors" />
            </button>
            <button
              type="button"
              onClick={handleExit}
              className="flex items-center gap-2 px-3 sm:px-4 h-10 rounded-xl bg-surface/60 hover:bg-surface border border-hairline hover:border-subtle transition-all duration-200"
            >
              <span className="text-sm text-secondary">Exit</span>
              <kbd className="hidden can-hover:inline text-[10px] text-muted bg-raised/50 px-1.5 py-0.5 rounded">
                Esc
              </kbd>
            </button>
          </div>
        </header>

        {/* Main content area */}
        {/* Phones stack the property panel under the preview as a bottom sheet */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
          {/* Preview container - generous padding, centered */}
          <div className="flex-1 min-h-0 flex items-center justify-center p-2 sm:p-8 lg:p-12">
            {/* Preview frame with premium shadow */}
            <div
              className="relative w-full h-full max-w-6xl rounded-2xl overflow-hidden"
              style={{
                boxShadow: `
                  0 0 0 1px rgba(255,255,255,0.04),
                  0 4px 8px -2px rgba(0,0,0,0.3),
                  0 16px 32px -8px rgba(0,0,0,0.4),
                  0 32px 64px -16px rgba(0,0,0,0.5)
                `,
              }}
            >
              {/* Browser chrome mockup */}
              <div className="hidden md:flex h-10 bg-[#1a1a1f] border-b border-[rgba(255,255,255,0.04)] items-center px-4 gap-3">
                {/* Traffic lights */}
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F56]" />
                  <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#27CA3F]" />
                </div>
                {/* URL bar */}
                <div className="flex-1 flex justify-center">
                  <div className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-[#0e0e12] border border-[rgba(255,255,255,0.04)] max-w-md w-full">
                    <div className="i-ph:lock-simple text-[10px] text-success" />
                    <span className="text-[11px] text-muted truncate font-mono">
                      {previewUrl.replace(/^https?:\/\//, '').split('/')[0]}
                    </span>
                  </div>
                </div>
                {/* Spacer */}
                <div className="w-[60px]" />
              </div>

              {/* Iframe container */}
              <div className="relative w-full h-full md:h-[calc(100%-40px)] bg-white">
                <iframe
                  ref={canvasIframeRef}
                  src={previewUrl}
                  className="w-full h-full border-0"
                  title="Canvas Preview"
                  onLoad={() => setIframeLoaded(true)}
                />

                {/* Loading overlay */}
                <AnimatePresence>
                  {!iframeLoaded && (
                    <motion.div
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0 flex items-center justify-center bg-page"
                    >
                      <div className="flex flex-col items-center gap-4">
                        <div className="relative">
                          <div className="w-12 h-12 rounded-2xl bg-accent/10 flex items-center justify-center">
                            <div className="i-svg-spinners:90-ring-with-bg text-2xl text-accent" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-sm text-primary">Loading preview</span>
                          <span className="text-xs text-muted">Preparing your canvas...</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Property panel - right side */}
          <CanvasPropertyPanel chatId={chatId} onReloadPreview={handleReload} />
        </div>

        {/* Bottom hint bar - floating, elegant. Keyboard hints mean nothing on touch. */}
        <div className="hidden can-hover:block absolute bottom-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.3 }}
            className="flex items-center gap-6 px-6 py-3 rounded-2xl bg-surface/70 backdrop-blur-xl border border-hairline"
            style={{
              boxShadow: '0 8px 32px -8px rgba(0,0,0,0.5)',
            }}
          >
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 rounded-md bg-raised border border-hairline text-[11px] text-secondary font-mono">
                Click
              </kbd>
              <span className="text-xs text-muted">select</span>
            </div>
            <div className="w-px h-4 bg-hairline" />
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 rounded-md bg-raised border border-hairline text-[11px] text-secondary font-mono">
                Esc
              </kbd>
              <span className="text-xs text-muted">deselect</span>
            </div>
            <div className="w-px h-4 bg-hairline" />
            <div className="flex items-center gap-2">
              <kbd className="px-2 py-1 rounded-md bg-raised border border-hairline text-[11px] text-secondary font-mono">
                Esc Esc
              </kbd>
              <span className="text-xs text-muted">exit</span>
            </div>
          </motion.div>
        </div>

        {/* Edit History Panel */}
        <EditHistoryPanel isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onReloadPreview={handleReload} />
      </motion.div>
    </AnimatePresence>
  );
}
